import { APP_NAME } from '@/config/productBrand';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { publicApi } from '@/api/public.api';
import { storage } from '@/lib/storage';

/** Generic fallback shown only when a tenant has no branding resolved (never the product name). */
export const FALLBACK_BRAND_NAME = APP_NAME;

/**
 * Tenant branding (company name, logo, colors) for the resolved tenant. Served by the public
 * (unauthenticated) endpoint so it works on the login screen AND across the authenticated app
 * without requiring any permission. Resolved by the X-Tenant header / subdomain.
 */
export function usePublicBranding() {
  return useQuery({
    queryKey: ['public-branding', storage.tenantCode],
    queryFn: publicApi.branding,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** Convenience accessor: branding with a safe company-name fallback. */
export function useBranding() {
  const q = usePublicBranding();
  return {
    companyName: q.data?.companyName?.trim() || FALLBACK_BRAND_NAME,
    hasCompanyName: !!q.data?.companyName?.trim(),
    primaryColor: q.data?.primaryColor,
    logoFileId: q.data?.logoFileId ?? null,
    data: q.data,
  };
}

/** Applies tenant branding to the browser tab (title + favicon) whenever it changes. */
export function useApplyBranding(enabled = true) {
  const { companyName, logoFileId } = useBranding();
  useEffect(() => {
    if (enabled && companyName) document.title = companyName;
  }, [enabled, companyName]);
  useEffect(() => {
    if (!enabled || !logoFileId) return;
    const link = (document.querySelector("link[rel='icon']") as HTMLLinkElement) ?? createIconLink();
    link.href = publicApi.logoUrl();
  }, [enabled, logoFileId]);
}

function createIconLink(): HTMLLinkElement {
  const link = document.createElement('link');
  link.rel = 'icon';
  document.head.appendChild(link);
  return link;
}
